import React, { PropsWithChildren, useState } from "react";

import { Button } from "@mui/material";
import { EmailInboxModal } from "components/Header/Email/EmailInbox";
import {
  Confirm,
  CreateInvestorModal,
  CreateModal,
  DeleteModal,
  ErrorModal,
  FileDelete,
  FilesUpload,
  InfoModal,
  Notice,
  RoomConnect,
  SelectDealFolderModal,
  SendQuestionModal,
  SuccessModal,
  UpgradeAccount,
  WarningModal
} from "components/modals";
import { ExpiredPasswordModal } from "components/modals/Expired-password-modal/ExpiredPasswordModal";
import { ImagePreviewModal } from "components/modals/Image-preview-modal/ImagePreviewModal";
import { IRRGraphModal } from "components/modals/IRR-graph-modal/IRRGraphModal";
import { PdfTaggerErrorModal } from "components/modals/Pdf-tagger-error-modal/PdfTaggerErrorModal";
import { UpgradePlanModal } from "components/modals/Upgrade-plan-modal/UpgradePlanModal";
import { Notice as NoticePrompt } from "components/Prompts/Notice";
import { BankPercentageDialog } from "components/reports/Report-button/BankPercentageDialog";
import { useIsViewerPermission, usePrompt } from "hooks";
import { useDataRoomsActions, usePlatformsSelect } from "store";
import { ApiValidationErrors, IFile } from "store/types";
import {
  BaseModal,
  IBankPercentageModal,
  IConfirmModal,
  ICreateModal,
  IDeleteModal,
  IImagePreviewModal,
  IInvestorModal,
  IInvestorVisualizationModal,
  IModals,
  ISelectDealFolderModal,
  IUseModals,
  IWarningModal
} from "store/types/modals";

import { DataroomInfoConnect } from "../../Dashboard/Dataroom-info-connect/DataroomInfoConnect";
import { InvestorVisualizationModal } from "../../modals/Investor-visualization-modal/InvestorVisualizationModal";
import { ModalsContext, modalsInitialState } from "./ModalsContext";

export const ModalsProvider: React.FC<PropsWithChildren> = ({ children }): JSX.Element => {
  const [modals, setModals] = useState<IModals>(modalsInitialState);
  const dataRoomsActions = useDataRoomsActions();
  const cachedPlatforms = usePlatformsSelect();
  const isAnalystViewerPermission = useIsViewerPermission("file_upload");
  const openPrompt = usePrompt();

  const useModals: IUseModals = {
    successModal: (successModal: BaseModal) => setModals({ ...modals, successModal }),
    closeSuccessModal: () => {
      modals.successModal?.action?.();
      setModals({ ...modals, successModal: null });
    },

    errorModal: (errorModal: BaseModal) => setModals({ ...modals, errorModal }),
    closeErrorModal: () => {
      modals.errorModal?.action?.();
      setModals({ ...modals, errorModal: null });
    },

    warningModal: (warningModal: IWarningModal) => setModals({ ...modals, warningModal }),
    closeWarningModal: () => {
      modals.warningModal?.action?.();
      setModals({ ...modals, warningModal: null });
    },
    cancelWarningModal: () => {
      modals.warningModal?.cancelAction?.();
      setModals({ ...modals, warningModal: null });
    },

    infoModal: (infoModal: BaseModal) => setModals({ ...modals, infoModal }),
    closeInfoModal: () => {
      modals.infoModal?.action?.();
      setModals({ ...modals, infoModal: null });
    },

    noticeModal: (noticeModal: BaseModal) => setModals({ ...modals, noticeModal }),
    closeNoticeModal: () => {
      modals.noticeModal?.action?.();
      setModals({ ...modals, noticeModal: null });
    },

    deleteModal: (deleteModal: IDeleteModal) => setModals({ ...modals, deleteModal }),
    closeDeleteModal: () => setModals({ ...modals, deleteModal: null }),
    createModal: (createModal: ICreateModal) => setModals({ ...modals, createModal }),
    closeCreateModal: () => setModals((prevState) => ({ ...prevState, createModal: null })),
    deleteFileModal: (file: IFile, cb?: () => void) => setModals({ ...modals, deleteFileModal: { file, cb } }),
    closeDeleteFileModal: () => setModals({ ...modals, deleteFileModal: null }),
    investorModal: (investorModal: IInvestorModal) => setModals({ ...modals, investorModal }),
    closeInvestorModal: () => setModals({ ...modals, investorModal: { isOpen: false } }),
    investorVisualizationModal: (investorModal: IInvestorVisualizationModal) =>
      setModals({ ...modals, investorVisualizationModal: investorModal }),
    closeInvestorVisualizationModal: () =>
      setModals({ ...modals, investorVisualizationModal: { isOpen: false, dealId: 0 } }),
    confirmModal: (conformModal: IConfirmModal) => setModals({ ...modals, confirmModal: conformModal }),
    closeConfirmModal: () => setModals({ ...modals, confirmModal: null }),
    expiredPasswordModal: (expiredPasswordModal: { isShow: boolean; onContinue: () => void }) =>
      setModals({ ...modals, expiredPasswordModal }),
    closeExpiredPasswordModal: () =>
      setModals({
        ...modals,
        expiredPasswordModal: { isShow: false, onContinue: () => {} }
      }),
    openImageModal: (imagePreviewModal: IImagePreviewModal) => setModals({ ...modals, imagePreviewModal }),
    closeImageModal: () => setModals({ ...modals, imagePreviewModal: { src: "" } }),
    sendUsQuestion: () => setModals((prev) => ({ ...prev, sendUsQuestion: true })),

    pdfTaggerErrorModal: (pdfTaggerErrorModal: ApiValidationErrors) => setModals({ ...modals, pdfTaggerErrorModal }),
    closePdfTaggerErrorModal: () => {
      setModals({ ...modals, pdfTaggerErrorModal: null });
    },

    openIRRGraphModal: (open: boolean) => setModals({ ...modals, irrGraphModalOpen: open }),
    openDataroomConnect: async (data) => {
      // Use cached platforms if available, otherwise fetch from API
      if (cachedPlatforms && cachedPlatforms.length > 0) {
        setModals((prevState) => ({
          ...prevState,
          dataroomConnect: { isOpen: true, ...data, platforms: cachedPlatforms }
        }));
      } else {
        try {
          // Fetch platforms before opening modal only if not cached
          const platforms = await dataRoomsActions.getPlatformsAction();
          setModals((prevState) => ({
            ...prevState,
            dataroomConnect: { isOpen: true, ...data, platforms }
          }));
        } catch (_error) {
          // Display error if fetching platforms fails
          setModals({
            ...modals,
            errorModal: {
              msg: "Failed to load platforms. Please try again."
            }
          });
        }
      }
    },
    closeDataroomConnect: () => {
      setModals((prevState) => ({ ...prevState, dataroomConnect: { isOpen: false } }));
    },
    openUpgradeAccount: () => {
      setModals((prevState) => ({ ...prevState, upgradeAccount: true }));
    },
    closeUpgradeAccount: () => {
      setModals((prevState) => ({ ...prevState, upgradeAccount: false }));
    },
    openUpgradePlanModal: () => {
      setModals((prevState) => ({ ...prevState, upgradePlanModal: true }));
    },
    closeUpgradePlanModal: () => {
      setModals((prevState) => ({ ...prevState, upgradePlanModal: false }));
    },
    openDataroomInfoConnect: async () => {
      if (cachedPlatforms && cachedPlatforms.length > 0) {
        setModals((prevState) => ({ ...prevState, dataroomInfoConnect: { isOpen: true } }));
      } else {
        try {
          await dataRoomsActions.getPlatformsAction();
          setModals((prevState) => ({ ...prevState, dataroomInfoConnect: { isOpen: true } }));
        } catch (_error) {
          setModals((prev) => ({ ...prev, errorModal: { msg: "Failed to load platforms. Please try again." } }));
        }
      }
    },
    closeDataroomInfoConnect: () => {
      setModals((prevState) => ({ ...prevState, dataroomInfoConnect: { isOpen: false } }));
    },

    isConnectDataroomsOpen: modals.dataroomConnect.isOpen,

    selectDealFolderModal: (data: ISelectDealFolderModal) => setModals({ ...modals, selectDealFolderModal: data }),
    closeSelectDealFolderModal: () =>
      setModals({ ...modals, selectDealFolderModal: { isOpen: false, title: "", onSubmit: () => {} } }),
    emailInboxModal: (data: { isFullScreenMode: boolean; anchorEl: HTMLElement } | null) =>
      setModals({ ...modals, emailInboxModal: data }),
    bankPercentageModal: (data: IBankPercentageModal) => setModals({ ...modals, bankPercentageModal: data }),
    closeBankPercentageModal: () => setModals({ ...modals, bankPercentageModal: null }),
    filesUploadModal: (
      data: {
        dealId?: number;
        folderId?: number;
        files?: File[];
        onSuccessCallback?: () => Promise<void>;
        openDataExtractor?: boolean;
        acceptFileTypes?: string;
        isGlobalWorkspace?: boolean;
      } = {}
    ) => {
      if (isAnalystViewerPermission) {
        openPrompt({
          Component: NoticePrompt,
          props: {
            type: "trial"
          }
        });
        return;
      }
      setModals({ ...modals, filesUploadModal: data });
    },
    closeFilesUploadModal: () => setModals({ ...modals, filesUploadModal: null })
  };

  return (
    <ModalsContext.Provider value={useModals}>
      {children}
      <CreateModal data={modals.createModal} />
      <DeleteModal data={modals.deleteModal} />
      <SuccessModal data={modals.successModal} />
      <ErrorModal data={modals.errorModal} />
      <PdfTaggerErrorModal data={modals.pdfTaggerErrorModal} />
      <FileDelete data={modals.deleteFileModal?.file ?? null} cb={modals.deleteFileModal?.cb} />
      <WarningModal data={modals.warningModal} />
      <InfoModal data={modals.infoModal} />
      <Notice data={modals.noticeModal} />
      <CreateInvestorModal {...modals.investorModal} />
      <InvestorVisualizationModal {...modals.investorVisualizationModal} />
      {modals.confirmModal && <Confirm {...modals.confirmModal} />}
      <ExpiredPasswordModal {...modals.expiredPasswordModal} />
      <ImagePreviewModal data={modals.imagePreviewModal} />
      <IRRGraphModal
        open={!!modals.irrGraphModalOpen}
        onClose={() => setModals({ ...modals, irrGraphModalOpen: false })}
      />
      <SendQuestionModal
        isOpen={modals.sendUsQuestion}
        onClose={() => setModals((prev) => ({ ...prev, sendUsQuestion: false }))}
      />
      <RoomConnect {...modals.dataroomConnect} onClose={() => useModals.closeDataroomConnect()} />
      <DataroomInfoConnect {...modals.dataroomInfoConnect} onClose={() => useModals.closeDataroomInfoConnect()} />
      <UpgradeAccount isOpen={modals.upgradeAccount} />
      <UpgradePlanModal
        isOpen={modals.upgradePlanModal}
        onClose={() => useModals.closeUpgradePlanModal()}
        manualUpgrade
      />
      <Button
        sx={{ visibility: "hidden", position: "absolute", top: 0 }}
        id="SendQuestionModal"
        onClick={useModals.sendUsQuestion}
      >
        SendQuestionModal Trigger
      </Button>
      <SelectDealFolderModal {...modals.selectDealFolderModal} />
      {modals.emailInboxModal && <EmailInboxModal {...modals.emailInboxModal} />}
      {modals.bankPercentageModal && (
        <BankPercentageDialog
          open
          banks={modals.bankPercentageModal.banks}
          savedPercentages={modals.bankPercentageModal.savedPercentages}
          onConfirm={(result) => {
            modals.bankPercentageModal?.action(result);
            setModals({ ...modals, bankPercentageModal: null });
          }}
          onCancel={() => {
            modals.bankPercentageModal?.onCancel?.();
            setModals({ ...modals, bankPercentageModal: null });
          }}
        />
      )}
      {modals.filesUploadModal && <FilesUpload {...modals.filesUploadModal} />}
    </ModalsContext.Provider>
  );
};
